import { useState, useCallback, useEffect, useRef } from 'react'
import { X, Plus } from 'lucide-react'
import { motion } from 'framer-motion'
import type { GCodeResponse } from '../../services/types'
import type { Operation } from '../../types'
import { highlightGCode } from '../../lib/gcode/highlight'
import { useWorkspaceEvents } from '../../contexts/WorkspaceEventContext'

interface CodeEditorProps {
  operations: Operation[]
  activeOperation: Operation | undefined
  onSelectOperation: (opId: string) => void
  onAddOperation: () => void
  onRemoveOperation: (opId: string) => void
  onCodeChange?: (gcode: GCodeResponse) => void
  readOnly?: boolean
  isPlanningOperations?: boolean
  planningText?: string
  highlightRange?: { startLine: number; endLine: number }
}

// ---- CodeEditor ----

export default function CodeEditor({
  operations,
  activeOperation,
  onSelectOperation,
  onAddOperation,
  onRemoveOperation,
  onCodeChange,
  readOnly,
  isPlanningOperations,
  planningText,
  highlightRange,
}: CodeEditorProps) {
  const { emit } = useWorkspaceEvents()
  const gcode = activeOperation?.gcode ?? null
  const isGenerating = activeOperation?.isGenerating ?? false
  const [editedCode, setEditedCode] = useState(gcode?.code ?? '')

  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const preRef = useRef<HTMLPreElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const planningEndRef = useRef<HTMLDivElement>(null)

  // Sync editedCode when gcode prop changes (e.g. regeneration)
  useEffect(() => {
    setEditedCode(gcode?.code ?? '')
  }, [gcode?.code])

  // Auto-scroll planning text as it streams in
  useEffect(() => {
    if (isPlanningOperations && planningText) {
      planningEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [isPlanningOperations, planningText])

  // Auto-scroll to highlighted range when it changes
  useEffect(() => {
    if (!highlightRange || !textareaRef.current) return
    const lineHeight = 24 // leading-6 = 1.5rem = 24px
    const scrollTo = (highlightRange.startLine - 1) * lineHeight - 48
    textareaRef.current.scrollTop = Math.max(0, scrollTo)
    if (preRef.current) preRef.current.scrollTop = textareaRef.current.scrollTop
    if (gutterRef.current) gutterRef.current.scrollTop = textareaRef.current.scrollTop
  }, [highlightRange])

  const lines = editedCode.split('\n')

  const handleCodeInput = useCallback((value: string) => {
    const oldCode = editedCode
    setEditedCode(value)
    if (!gcode || !onCodeChange) return
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      onCodeChange({
        ...gcode,
        code: value,
        lines: value.split('\n').length,
      })

      // Emit event so the AI sees this edit
      const oldLines = oldCode.split('\n')
      const newLines = value.split('\n')
      const changedCount = newLines.filter((l, i) => l !== (oldLines[i] ?? '')).length +
        Math.abs(newLines.length - oldLines.length)
      if (changedCount > 0) {
        // Build a short summary of what changed
        const diffs: string[] = []
        for (let i = 0; i < Math.max(oldLines.length, newLines.length); i++) {
          if ((oldLines[i] ?? '') !== (newLines[i] ?? '')) {
            diffs.push(`L${i + 1}: "${(oldLines[i] ?? '').trim()}" → "${(newLines[i] ?? '').trim()}"`)
            if (diffs.length >= 3) { diffs.push('...'); break }
          }
        }
        emit({
          type: 'code_edit',
          operationId: activeOperation?.label ?? activeOperation?.id ?? 'unknown',
          summary: diffs.join(', '),
          linesChanged: changedCount,
        })
      }
    }, 500)
  }, [gcode, onCodeChange, editedCode, activeOperation, emit])

  const handleScroll = useCallback(() => {
    const ta = textareaRef.current
    if (!ta) return
    if (preRef.current) {
      preRef.current.scrollTop = ta.scrollTop
      preRef.current.scrollLeft = ta.scrollLeft
    }
    if (gutterRef.current) {
      gutterRef.current.scrollTop = ta.scrollTop
    }
  }, [])

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      const ta = e.currentTarget
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const value = ta.value
      const newValue = value.substring(0, start) + '  ' + value.substring(end)
      handleCodeInput(newValue)
      requestAnimationFrame(() => {
        ta.selectionStart = ta.selectionEnd = start + 2
      })
    }
  }, [handleCodeInput])

  // Strip JSON block from planning text for display
  const displayPlanningText = planningText
    ? planningText.replace(/```json[\s\S]*$/, '').trim()
    : ''

  return (
    <div className="flex flex-col h-full glass rounded-t-xl">
      {/* Operation sub-tabs */}
      {operations.length > 0 && (
        <div className="flex items-center gap-1.5 px-4 border-b border-cyan-500/10 flex-shrink-0 overflow-x-auto" style={{ height: '2.25rem' }}>
          {operations.map(op => {
            const isActive = op.id === activeOperation?.id
            return (
              <button
                key={op.id}
                onClick={() => onSelectOperation(op.id)}
                className={`
                  group relative flex items-center gap-1.5
                  px-3 py-1 font-mono text-xs rounded-lg
                  transition-all duration-150 flex-shrink-0
                  ${isActive
                    ? 'text-cyan-300 bg-cyan-500/10 border border-cyan-500/30'
                    : 'text-slate-500 border border-transparent hover:border-cyan-500/20 hover:text-slate-300'
                  }
                `}
              >
                <span className="truncate" style={{ maxWidth: '160px' }}>{op.label}</span>
                {op.isGenerating && (
                  <motion.span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                )}
                {!readOnly && operations.length > 1 && (
                  <span
                    role="button"
                    onClick={e => { e.stopPropagation(); onRemoveOperation(op.id) }}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 hover:text-slate-100"
                  >
                    <X className="w-3 h-3" />
                  </span>
                )}
              </button>
            )
          })}
          {!readOnly && (
            <button
              onClick={onAddOperation}
              className="flex items-center gap-1 px-2 py-1 rounded-lg font-mono text-xs text-slate-600 hover:text-slate-400 hover:bg-white/5 transition-all duration-150 flex-shrink-0"
            >
              <Plus className="w-3 h-3" /> Add Op
            </button>
          )}
        </div>
      )}

      {/* Code body */}
      <div className="flex-1 overflow-y-auto relative">
        {gcode ? (
          <div className="flex min-h-full h-full">
            {/* Line numbers (synced scroll) */}
            <div
              ref={gutterRef}
              className="flex-shrink-0 select-none py-4 px-3 text-right border-r border-cyan-500/10 overflow-hidden"
              style={{ minWidth: '3.5rem', background: 'rgba(3,7,18,0.4)' }}
            >
              {lines.map((_, i) => {
                const isHL = highlightRange &&
                  (i + 1) >= highlightRange.startLine &&
                  (i + 1) <= highlightRange.endLine
                return (
                  <div key={i} className={`font-mono text-xs leading-6 ${isHL ? 'text-cyan-300 bg-cyan-500/10' : 'text-slate-700'}`}>
                    {i + 1}
                  </div>
                )
              })}
            </div>

            {/* Code overlay container */}
            <div className="flex-1 relative min-w-0">
              {/* Layer 1: Syntax-highlighted pre (behind) */}
              <pre
                ref={preRef}
                aria-hidden="true"
                className="absolute inset-0 py-4 px-4 font-mono text-xs leading-6 overflow-auto pointer-events-none m-0 whitespace-pre"
              >
                {lines.map((line, i) => {
                  const isHL = highlightRange &&
                    (i + 1) >= highlightRange.startLine &&
                    (i + 1) <= highlightRange.endLine
                  return (
                    <div key={i} className={`px-1 -mx-1 ${isHL ? 'bg-cyan-500/10' : ''}`}>
                      {highlightGCode(line)}
                    </div>
                  )
                })}
              </pre>

              {/* Layer 2: Editable textarea (on top) */}
              <textarea
                ref={textareaRef}
                value={editedCode}
                onChange={e => handleCodeInput(e.target.value)}
                onScroll={handleScroll}
                onKeyDown={handleKeyDown}
                spellCheck={false}
                readOnly={readOnly}
                wrap="off"
                className="absolute inset-0 w-full h-full py-4 px-4 font-mono text-xs leading-6 bg-transparent text-transparent caret-cyan-400 selection:bg-cyan-500/30 resize-none outline-none overflow-auto m-0 border-0 whitespace-pre"
                style={{ caretColor: readOnly ? 'transparent' : '#22d3ee' }}
              />
            </div>
          </div>
        ) : isGenerating ? (
          <div className="h-full flex flex-col items-center justify-center gap-4">
            <div className="relative w-48 h-px bg-cyan-500/20 overflow-hidden rounded-full">
              <motion.div
                className="absolute inset-y-0 w-16 rounded-full"
                style={{ background: 'linear-gradient(90deg, transparent, #06b6d4, transparent)' }}
                animate={{ x: [-64, 192] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
              />
            </div>
            <span className="font-mono text-xs text-slate-500 tracking-widest animate-pulse">
              GENERATING G-CODE...
            </span>
            <div className="flex gap-1">
              {[0, 1, 2].map(i => (
                <motion.div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full bg-cyan-500/40"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                />
              ))}
            </div>
          </div>
        ) : isPlanningOperations ? (
          <div className="h-full flex flex-col p-6 overflow-y-auto">
            {/* Streaming planning text */}
            {displayPlanningText ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <motion.span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <span className="font-mono text-[10px] text-cyan-400/70 tracking-widest uppercase">
                    AI is planning operations & tooling...
                  </span>
                </div>
                <div className="font-mono text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {displayPlanningText}
                  <motion.span
                    className="inline-block w-1.5 h-4 bg-cyan-400 rounded-sm ml-0.5 align-middle"
                    animate={{ opacity: [1, 0, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  />
                </div>
                <div ref={planningEndRef} />
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-4">
                <div className="relative w-48 h-px bg-cyan-500/20 overflow-hidden rounded-full">
                  <motion.div
                    className="absolute inset-y-0 w-16 rounded-full"
                    style={{ background: 'linear-gradient(90deg, transparent, #06b6d4, transparent)' }}
                    animate={{ x: [-64, 192] }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                  />
                </div>
                <span className="font-mono text-xs text-slate-500 tracking-widest animate-pulse">
                  PLANNING OPERATIONS...
                </span>
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => (
                    <motion.div
                      key={i}
                      className="w-1.5 h-1.5 rounded-full bg-cyan-500/40"
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <span className="font-mono text-xs text-slate-600">No G-code generated yet</span>
          </div>
        )}
      </div>
    </div>
  )
}
